import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Field, TextInput, TextArea, SelectInput, Loader, EmptyState } from "../components/Primitives";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const STATUS_META = {
  provided: { label: "Provided", color: "#059669", bg: "rgba(5,150,105,0.10)", icon: "CheckCircle2" },
  verified: { label: "Verified", color: "#1D4ED8", bg: "rgba(29,78,216,0.10)", icon: "BadgeCheck" },
  unknown: { label: "Unknown", color: "#6B7280", bg: "rgba(107,114,128,0.10)", icon: "HelpCircle" },
  not_applicable: { label: "N/A", color: "#6B7280", bg: "rgba(107,114,128,0.10)", icon: "MinusCircle" },
  evidence_required: { label: "Evidence needed", color: "#D97706", bg: "rgba(217,119,6,0.10)", icon: "FileWarning" },
};
const STATUS_KEYS = ["provided", "verified", "unknown", "not_applicable", "evidence_required"];

// Render an input based on field type
function FieldInput({ field, value, onChange }) {
  const t = field.type;
  if (t === "textarea") {
    return <TextArea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      data-testid={`cp-input-${field.key}`} placeholder={field.help || ""} />;
  }
  if (t === "select") {
    return (
      <SelectInput value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid={`cp-input-${field.key}`}>
        <option value="">— Select —</option>
        {field.options?.map((o) => <option key={o} value={o}>{o}</option>)}
      </SelectInput>
    );
  }
  if (t === "multiselect") {
    const arr = Array.isArray(value) ? value : (typeof value === "string" && value ? value.split(",").map((s) => s.trim()) : []);
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`cp-input-${field.key}`}>
        {field.options?.map((o) => {
          const active = arr.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(active ? arr.filter((x) => x !== o) : [...arr, o])}
              className="px-2.5 py-1 text-[11px] rounded-full border transition-all duration-150"
              style={active
                ? { background: "var(--primary)", color: "white", borderColor: "var(--primary)" }
                : { background: "var(--surface)", color: "var(--ink)", borderColor: "var(--border)" }}
            >
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  if (t === "boolean") {
    const bv = value === true || value === "true";
    const nv = value === false || value === "false";
    return (
      <div className="flex gap-2" data-testid={`cp-input-${field.key}`}>
        <button type="button" onClick={() => onChange(true)}
          className="px-3 py-1.5 text-xs rounded-lg border transition-all"
          style={bv ? { background: "#059669", color: "white", borderColor: "#059669" } : { background: "var(--surface)", color: "var(--ink)", borderColor: "var(--border)" }}>
          <Icons.Check className="w-3 h-3 inline mr-1" /> Yes
        </button>
        <button type="button" onClick={() => onChange(false)}
          className="px-3 py-1.5 text-xs rounded-lg border transition-all"
          style={nv ? { background: "#DC2626", color: "white", borderColor: "#DC2626" } : { background: "var(--surface)", color: "var(--ink)", borderColor: "var(--border)" }}>
          <Icons.X className="w-3 h-3 inline mr-1" /> No
        </button>
      </div>
    );
  }
  if (t === "list_text") {
    const disp = Array.isArray(value) ? value.join(", ") : (value ?? "");
    return (
      <TextInput
        value={disp}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw.split(",").map((s) => s.trim()).filter(Boolean));
        }}
        placeholder={field.help || "Comma-separated values"}
        data-testid={`cp-input-${field.key}`}
      />
    );
  }
  if (t === "number") {
    return <TextInput type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`cp-input-${field.key}`} />;
  }
  return <TextInput type={t} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`cp-input-${field.key}`} placeholder={field.help || ""} />;
}


function StatusPill({ status, small }) {
  const m = STATUS_META[status] || STATUS_META.unknown;
  const Ico = Icons[m.icon] || Icons.Circle;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${small ? "px-1.5 py-[1px] text-[9.5px]" : "px-2 py-0.5 text-[10.5px]"}`}
      style={{ background: m.bg, color: m.color }}>
      <Ico className={small ? "w-2.5 h-2.5" : "w-3 h-3"} /> {m.label}
    </span>
  );
}


function FieldRow({ field, fieldValue, evidenceCount, onSave, onOpenEvidence }) {
  const initialValue = fieldValue?.value ?? (field.type === "multiselect" || field.type === "list_text" ? [] : "");
  const [value, setValue] = useState(initialValue);
  const [status, setStatus] = useState(fieldValue?.status || "unknown");
  const [notes, setNotes] = useState(fieldValue?.notes || "");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const dirty = useMemo(() => {
    if (JSON.stringify(value) !== JSON.stringify(initialValue)) return true;
    if (status !== (fieldValue?.status || "unknown")) return true;
    if (notes !== (fieldValue?.notes || "")) return true;
    return false;
  }, [value, status, notes, fieldValue, initialValue]);

  const doSave = async () => {
    setSaving(true);
    await onSave({ field_key: field.key, value, status, notes });
    setSaving(false);
  };

  return (
    <div className="rounded-lg card-hover" style={{ border: "1px solid var(--border)" }} data-testid={`cp-row-${field.key}`}>
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[13px] font-medium ink">{field.label}</span>
              {field.required && <span className="danger text-[10px]">*</span>}
              <StatusPill status={status} small />
              {evidenceCount > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9.5px] font-medium"
                  style={{ background: "rgba(79,70,229,0.10)", color: "var(--primary)" }}>
                  <Icons.Paperclip className="w-2.5 h-2.5" /> {evidenceCount}
                </span>
              )}
            </div>
            {field.help && <div className="text-[11px] muted mt-1">{field.help}</div>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => onOpenEvidence(field)}
              className="p-1.5 rounded-md hover:bg-app-2 muted hover:ink transition-colors"
              title="Evidence"
              data-testid={`cp-evidence-btn-${field.key}`}
            >
              <Icons.Upload className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setExpanded((x) => !x)}
              className="p-1.5 rounded-md hover:bg-app-2 muted hover:ink transition-colors"
              title={expanded ? "Collapse" : "Expand"}
              data-testid={`cp-expand-${field.key}`}
            >
              <Icons.ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>
        <FieldInput field={field} value={value} onChange={setValue} />
        {expanded && (
          <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: "var(--border)" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Field label="Status">
                <SelectInput value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`cp-status-${field.key}`}>
                  {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
                </SelectInput>
              </Field>
              <Field label="Last updated">
                <div className="text-[11px] muted py-2">
                  {fieldValue?.updated_at ? new Date(fieldValue.updated_at).toLocaleString() : "—"}
                  {fieldValue?.updated_by && <span> · by {fieldValue.updated_by}</span>}
                </div>
              </Field>
            </div>
            <Field label="Notes / justification">
              <TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add context, references, or reasoning..." data-testid={`cp-notes-${field.key}`} />
            </Field>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-2">
          {dirty && <span className="text-[10.5px] muted">unsaved</span>}
          <Btn size="sm" variant={dirty ? "primary" : "secondary"} disabled={!dirty || saving} onClick={doSave} data-testid={`cp-save-${field.key}`}>
            {saving ? <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icons.Save className="w-3.5 h-3.5" />}
            Save
          </Btn>
        </div>
      </div>
    </div>
  );
}


function EvidenceDrawer({ field, onClose, onUpload, onDelete, items, loading }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [validTill, setValidTill] = useState("");

  const doUpload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.info("Choose a file first");
    setUploading(true);
    try {
      await onUpload(f, label, validTill);
      setLabel(""); setValidTill("");
      if (fileRef.current) fileRef.current.value = "";
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={!!field} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="cp-evidence-drawer">
        <DialogHeader>
          <DialogTitle className="text-base">Evidence · {field?.label}</DialogTitle>
          <DialogDescription className="text-xs">
            Upload policy documents, screenshots, contracts, certificates. Max 20 MB per file. Files are stored securely with SHA-256 integrity.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg p-3" style={{ border: "1px dashed var(--border-strong)", background: "var(--bg-2)" }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Field label="Label (optional)">
                <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. ISO 27001 certificate" data-testid="cp-ev-label" />
              </Field>
              <Field label="Valid till (optional)">
                <TextInput type="date" value={validTill} onChange={(e) => setValidTill(e.target.value)} data-testid="cp-ev-validtill" />
              </Field>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input ref={fileRef} type="file" className="text-xs flex-1" data-testid="cp-ev-file" />
              <Btn size="sm" onClick={doUpload} disabled={uploading} data-testid="cp-ev-upload">
                {uploading ? <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icons.UploadCloud className="w-3.5 h-3.5" />} Upload
              </Btn>
            </div>
          </div>

          <div className="text-[11px] font-semibold uppercase tracking-wider muted mt-2">Uploaded ({items.length})</div>
          {loading ? <Loader /> : (
            items.length === 0 ? (
              <EmptyState title="No evidence yet" hint="Upload proof of compliance to unlock the 'Verified' status." />
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {items.map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between p-2.5 rounded-lg" style={{ border: "1px solid var(--border)" }}>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-medium ink truncate">{ev.label || ev.filename}</div>
                      <div className="text-[10.5px] muted">
                        {(ev.size / 1024).toFixed(1)} KB · {ev.content_type?.split("/")[1] || "file"}
                        · {new Date(ev.uploaded_at).toLocaleDateString()}
                        {ev.valid_till && <> · expires {new Date(ev.valid_till).toLocaleDateString()}</>}
                      </div>
                    </div>
                    <button onClick={() => onDelete(ev.id)} className="p-1.5 rounded hover:bg-app-2 danger transition-colors" title="Delete" data-testid={`cp-ev-del-${ev.id}`}>
                      <Icons.Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
        <DialogFooter>
          <Btn variant="secondary" size="sm" onClick={onClose} data-testid="cp-ev-close">Close</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AIInsightsPanel({ open, onClose, insights, loading }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="cp-ai-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Icons.Sparkles className="w-4 h-4" style={{ color: "var(--primary)" }} /> AI Analysis · Company Profile
          </DialogTitle>
          <DialogDescription className="text-xs">
            Grounded on the fields you've completed. AI never invents answers — missing data is surfaced explicitly.
          </DialogDescription>
        </DialogHeader>
        {loading ? <Loader /> : insights ? (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="rounded-lg p-3 aurora-tight" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-1">Summary</div>
              <div className="text-[13px] ink leading-relaxed">{insights.summary}</div>
            </div>

            {insights.recommended_focus_areas?.length > 0 && (
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Recommended focus</div>
                <div className="space-y-2">
                  {insights.recommended_focus_areas.map((r, i) => (
                    <div key={i} className="p-2.5 rounded-lg" style={{ border: "1px solid var(--border)" }}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icons.Target className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
                        <span className="text-[12.5px] font-semibold ink">{r.topic}</span>
                        {r.dpdp_ref && <span className="text-[9.5px] mono muted">{r.dpdp_ref}</span>}
                      </div>
                      <div className="text-[11.5px] muted pl-5">{r.why}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {insights.next_best_actions?.length > 0 && (
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Next best actions</div>
                <ol className="list-decimal ml-5 space-y-1 text-[12.5px] ink">
                  {insights.next_best_actions.map((a, i) => <li key={i}>{a}</li>)}
                </ol>
              </div>
            )}

            {insights.missing_documents?.length > 0 && (
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Documents you should upload next</div>
                <div className="flex flex-wrap gap-1.5">
                  {insights.missing_documents.map((d, i) => (
                    <span key={i} className="px-2 py-0.5 text-[10.5px] rounded-full" style={{ background: "rgba(217,119,6,0.10)", color: "#D97706" }}>
                      <Icons.FileX className="w-3 h-3 inline mr-1" />{d}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {insights.risk_signals?.length > 0 && (
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Risk signals</div>
                <div className="space-y-1.5">
                  {insights.risk_signals.map((r, i) => {
                    const c = r.severity === "critical" ? "#DC2626" : r.severity === "high" ? "#D97706" : r.severity === "medium" ? "#0891B2" : "#6B7280";
                    return (
                      <div key={i} className="flex items-start gap-2 p-2 rounded" style={{ background: `${c}10`, border: `1px solid ${c}30` }}>
                        <Icons.AlertTriangle className="w-3.5 h-3.5 mt-0.5" style={{ color: c }} />
                        <div>
                          <div className="text-[12.5px] font-medium" style={{ color: c }}>{r.signal}</div>
                          <div className="text-[11px] muted">{r.why}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {insights.applicability_flags?.length > 0 && (
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider muted mb-2">Derived applicability</div>
                <div className="flex flex-wrap gap-1.5">
                  {insights.applicability_flags.map((f) => (
                    <span key={f} className="px-2 py-0.5 text-[10.5px] rounded-full mono" style={{ background: "rgba(29,78,216,0.10)", color: "#1D4ED8" }}>
                      {f.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
                <div className="text-[10.5px] muted mt-1.5">
                  These flags drive which DPDP sections and questions apply to your organization.
                </div>
              </div>
            )}

            {insights.ai_available === false && (
              <div className="text-[10.5px] muted italic">
                (AI narrative unavailable — showing deterministic template. Re-run once the LLM key is available.)
              </div>
            )}
          </div>
        ) : <div className="text-sm muted py-6 text-center">No insights yet — click "Run AI Analysis" to generate.</div>}
        <DialogFooter>
          <Btn variant="secondary" size="sm" onClick={onClose} data-testid="cp-ai-close">Close</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function CompanyProfile() {
  const [schema, setSchema] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activeSection, setActiveSection] = useState("basic");
  const [loading, setLoading] = useState(true);
  const [evidenceField, setEvidenceField] = useState(null);
  const [evidenceItems, setEvidenceItems] = useState({});   // {field_key: [ev]}
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: sc }, { data: pr }, { data: ev }] = await Promise.all([
        api.get("/company-profile/schema"),
        api.get("/company-profile"),
        api.get("/company-profile/evidence"),
      ]);
      setSchema(sc); setProfile(pr);
      const byField = {};
      (ev || []).forEach((e) => {
        (byField[e.field_key] ||= []).push(e);
      });
      setEvidenceItems(byField);
    } catch (e) {
      toast.error("Failed to load company profile");
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const saveField = async (payload) => {
    try {
      const { data } = await api.put("/company-profile/field", payload);
      setProfile((p) => ({
        ...p,
        fields: { ...(p?.fields || {}), [payload.field_key]: data.field_value },
        completeness: data.completeness,
        applicability: data.applicability,
      }));
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const openEvidence = (field) => {
    setEvidenceField(field);
  };

  const uploadEvidence = async (file, label, validTill) => {
    const fd = new FormData();
    fd.append("field_key", evidenceField.key);
    fd.append("file", file);
    fd.append("label", label || "");
    fd.append("valid_till", validTill || "");
    setEvidenceLoading(true);
    try {
      const { data } = await api.post("/company-profile/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEvidenceItems((prev) => ({
        ...prev,
        [evidenceField.key]: [data, ...(prev[evidenceField.key] || [])],
      }));
      // Nudge status to evidence_required → provided
      setProfile((p) => {
        const cur = p?.fields?.[evidenceField.key] || {};
        const ids = [...(cur.evidence_ids || []), data.id];
        return { ...p, fields: { ...(p?.fields || {}), [evidenceField.key]: { ...cur, evidence_ids: ids } } };
      });
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setEvidenceLoading(false);
    }
  };

  const deleteEvidence = async (eid) => {
    try {
      await api.delete(`/company-profile/evidence/${eid}`);
      setEvidenceItems((prev) => {
        const cp = { ...prev };
        Object.keys(cp).forEach((k) => { cp[k] = cp[k].filter((x) => x.id !== eid); });
        return cp;
      });
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const runAi = async () => {
    setAiOpen(true); setAiLoading(true);
    try {
      const { data } = await api.post("/company-profile/ai-analyze");
      setAiInsights(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analysis failed");
    }
    setAiLoading(false);
  };

  if (loading) return <div className="p-8"><Loader /></div>;
  if (!schema || !profile) return <div className="p-8"><EmptyState title="Could not load schema" /></div>;

  const overall = profile?.completeness?.overall_pct || 0;
  const scoreColor = overall >= 80 ? "#059669" : overall >= 60 ? "#D97706" : overall >= 40 ? "#0891B2" : "#DC2626";
  const activeSchema = schema.sections.find((s) => s.key === activeSection) || schema.sections[0];
  const applicability = profile.applicability || {};
  const activeFlags = Object.entries(applicability).filter(([, v]) => v).map(([k]) => k);

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <PageHeader
        kicker="Onboarding · Company profile"
        title="Company Profile"
        description="A single source of truth about your organization. Completing this profile lets the platform dynamically tailor DPDP assessment questions, applicability rules, and evidence requirements to your business."
        testId="company-profile-page"
        actions={
          <>
            <Btn variant="secondary" size="sm" onClick={() => (window.location.href = "/app/assessment")} data-testid="cp-goto-assessment"><Icons.ClipboardCheck className="w-3.5 h-3.5" /> Continue to Assessment</Btn>
            <Btn variant="secondary" size="sm" onClick={loadAll} data-testid="cp-reload"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
            <Btn size="sm" onClick={runAi} data-testid="cp-ai-btn"><Icons.Sparkles className="w-3.5 h-3.5" /> AI Analysis</Btn>
          </>
        }
      />

      {/* Hero — overall completeness + applicability */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-1 aurora rounded-2xl p-6 card-shadow-lg stagger-in relative overflow-hidden" style={{ border: "1px solid var(--border)", "--stagger": 0 }}>
          <div className="label-cap mb-3">Profile completeness</div>
          <div className="flex items-baseline gap-2">
            <div className="num-display text-5xl leading-none" style={{ color: scoreColor }}>{overall}</div>
            <div className="text-lg font-semibold" style={{ color: scoreColor }}>%</div>
          </div>
          <div className="text-[11.5px] muted mt-2">
            {profile.completeness?.filled_count} of {profile.completeness?.total_count} fields completed
          </div>
          <div className="mt-4 h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${overall}%`, background: `linear-gradient(90deg, ${scoreColor}, ${scoreColor}dd)` }} />
          </div>
        </div>

        <div className="lg:col-span-2 surface rounded-2xl p-6 card-shadow card-hover stagger-in" style={{ border: "1px solid var(--border)", "--stagger": 1 }}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="label-cap mb-1">Derived DPDP applicability</div>
              <div className="text-[13px] ink">
                {activeFlags.length === 0 ? "Complete more fields to unlock tailored assessment routing." :
                  `${activeFlags.length} flags detected — DPDP assessment will be filtered accordingly.`}
              </div>
            </div>
            <Icons.Compass className="w-5 h-5 muted-2" strokeWidth={1.75} />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {activeFlags.length === 0 && <span className="text-[11px] muted italic">— no flags yet —</span>}
            {activeFlags.map((f) => (
              <span key={f} className="px-2 py-0.5 text-[10.5px] rounded-full mono font-medium"
                style={{ background: "rgba(29,78,216,0.10)", color: "#1D4ED8" }}>
                {f.replace(/_/g, " ")}
              </span>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-5 gap-2">
            {schema.sections.map((s) => {
              const p = profile.completeness?.section_pcts?.[s.key] || { pct: 0 };
              const Ico = Icons[s.icon] || Icons.Folder;
              return (
                <button key={s.key} onClick={() => setActiveSection(s.key)}
                  className={`text-left p-2 rounded-lg transition-all duration-150 ${activeSection === s.key ? "" : "hover:bg-app-2"}`}
                  style={activeSection === s.key
                    ? { background: "rgba(79,70,229,0.08)", border: "1px solid rgba(79,70,229,0.25)" }
                    : { background: "transparent", border: "1px solid var(--border)" }}
                  data-testid={`cp-nav-${s.key}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Ico className="w-3.5 h-3.5" style={{ color: activeSection === s.key ? "var(--primary)" : "var(--muted-color, #6B7280)" }} />
                    <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: activeSection === s.key ? "var(--primary)" : "var(--muted-color, #6B7280)" }}>
                      {s.title.split(" ")[0]}
                    </div>
                  </div>
                  <div className="text-[16px] font-bold tnum ink">{p.pct}%</div>
                  <div className="text-[9.5px] muted">{p.filled}/{p.total} filled</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active section — fields */}
      <div className="surface rounded-2xl p-6 card-shadow" style={{ border: "1px solid var(--border)" }}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {(() => { const Ico = Icons[activeSchema.icon] || Icons.Folder; return <Ico className="w-5 h-5" style={{ color: "var(--primary)" }} />; })()}
              <h2 className="text-lg font-semibold ink tracking-tight">{activeSchema.title}</h2>
            </div>
            <div className="text-[11.5px] muted">
              Complete each field, set an appropriate status, and attach evidence where relevant.
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {STATUS_KEYS.map((k) => <StatusPill key={k} status={k} />)}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {activeSchema.fields.map((f) => (
            <FieldRow
              key={f.key}
              field={f}
              fieldValue={profile.fields?.[f.key]}
              evidenceCount={evidenceItems[f.key]?.length || 0}
              onSave={saveField}
              onOpenEvidence={openEvidence}
            />
          ))}
        </div>
      </div>

      <EvidenceDrawer
        field={evidenceField}
        onClose={() => setEvidenceField(null)}
        onUpload={uploadEvidence}
        onDelete={deleteEvidence}
        items={evidenceItems[evidenceField?.key] || []}
        loading={evidenceLoading}
      />

      <AIInsightsPanel
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        insights={aiInsights}
        loading={aiLoading}
      />
    </div>
  );
}
